import { t } from "../i18n";

// tiny safe maths parser, no eval
// grammar: sum = product (+|- product)*, product = unary (*|/ unary)*, unary = -unary | power
// power = percent (^ unary)?, percent = primary %?, primary = number | ( sum )

class Parser {
  private position = 0;

  constructor(private text: string) {}

  parse(): number {
    const value = this.sum();
    if (this.position < this.text.length) throw new Error(t("unexpected \"{char}\"", { char: this.text[this.position] }));
    return value;
  }

  private peek(): string {
    return this.text[this.position] ?? "";
  }

  private take(symbol: string): boolean {
    if (this.peek() !== symbol) return false;
    this.position += 1;
    return true;
  }

  private sum(): number {
    let value = this.product();
    while (true) {
      if (this.take("+")) value += this.product();
      else if (this.take("-")) value -= this.product();
      else return value;
    }
  }

  private product(): number {
    let value = this.unary();
    while (true) {
      if (this.take("*")) value *= this.unary();
      else if (this.take("/")) value /= this.unary();
      else return value;
    }
  }

  // minus binds looser than powers so -3^2 is -9 like on paper
  private unary(): number {
    if (this.take("-")) return -this.unary();
    if (this.take("+")) return this.unary();
    return this.power();
  }

  // right associative so 2^3^2 is 2^9, the exponent may be negative
  private power(): number {
    const base = this.percent();
    return this.take("^") ? base ** this.unary() : base;
  }

  // 15% means 0.15, so 200*15% is 30
  private percent(): number {
    const value = this.primary();
    return this.take("%") ? value / 100 : value;
  }

  private primary(): number {
    if (this.take("(")) {
      const value = this.sum();
      if (!this.take(")")) throw new Error(t("missing )"));
      return value;
    }
    const match = this.text.slice(this.position).match(/^\d*\.?\d+(e[+-]?\d+)?/i);
    if (!match) throw new Error(t("expected a number"));
    this.position += match[0].length;
    return Number(match[0]);
  }
}

export function calculate(expression: string): number {
  const cleaned = expression.replace(/\s+/g, "").replace(/×/g, "*").replace(/÷/g, "/").replace(/−/g, "-").replace(/,/g, "");
  if (!cleaned) return 0;
  const result = new Parser(cleaned).parse();
  if (!Number.isFinite(result)) throw new Error(t("that goes to infinity"));
  return result;
}

export function formatResult(value: number): string {
  return Number.isInteger(value) ? value.toLocaleString("en-US") : Number(value.toPrecision(12)).toString();
}
